import { apiService } from './api';

export interface LocationSubmission {
  id: string;
  submitted_by: string;
  name: string;
  description: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  proposed_address: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    postal_code?: string;
    formatted_address?: string;
  };
  contact_info?: {
    phone?: string;
    email?: string;
    website?: string;
  };
  image_urls: string[];
  category?: string;
  tags: string[];
  cuisine_types: string[];
  status: 'pending' | 'approved' | 'rejected' | 'needs_revision';
  admin_review?: {
    reviewed_by: string;
    reviewed_at: string;
    action: string;
    notes?: string;
    modifications?: Record<string, any>;
  };
  submission_notes?: string;
  created_at: string;
  updated_at: string;
}

export interface Location {
  id: string;
  name: string;
  description: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  address: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    postal_code?: string;
    formatted_address?: string;
  };
  contact_info?: {
    phone?: string;
    email?: string;
    website?: string;
  };
  status: 'pending' | 'approved' | 'rejected' | 'suspended' | 'archived';
  submitted_by: string;
  approved_by?: string;
  approved_at?: string;
  image_urls: string[];
  category?: string;
  tags: string[];
  cuisine_types: string[];
  stats: {
    average_rating: number;
    total_reviews: number;
    hunt_usage_count: number;
    total_visits: number;
  };
  created_at: string;
  updated_at: string;
}

export interface LocationAnalytics {
  total_locations: number;
  locations_by_status: Record<string, number>;
  locations_by_category: Record<string, number>;
  submission_trends: Record<string, number>;
  top_submitters: Array<{
    user_id: string;
    submission_count: number;
  }>;
  usage_statistics: {
    total_reviews: number;
    total_hunt_usage: number;
    average_rating: number;
    locations_with_reviews: number;
  };
  external_service_coverage: Record<string, number>;
}

export interface AdminReviewRequest {
  action: 'approve' | 'reject' | 'request_revision' | 'suspend';
  notes?: string;
  modifications?: Record<string, any>;
}

export interface BulkLocationAction {
  location_ids: string[];
  action: 'approve' | 'reject' | 'suspend' | 'activate' | 'enrich';
  reason?: string;
}

class LocationService {
  // Get pending location submissions
  async getLocationSubmissions(params?: {
    status?: string;
    submitted_by?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    submissions: LocationSubmission[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.submitted_by) queryParams.append('submitted_by', params.submitted_by);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());

    const response: any = await apiService.get(`/admin/locations/submissions?${queryParams.toString()}`);
    return {
      submissions: response.submissions || [],
      total: response.total || 0,
      page: response.page || 1,
      page_size: response.page_size || 20,
      total_pages: response.total_pages || 1,
      has_next: response.has_next || false,
      has_previous: response.has_previous || false
    };
  }

  // Get specific submission details
  async getSubmissionDetails(submissionId: string): Promise<{
    submission: LocationSubmission;
  }> {
    return apiService.get(`/admin/locations/submissions/${submissionId}`);
  }

  // Review a location submission
  async reviewSubmission(submissionId: string, review: AdminReviewRequest): Promise<{
    success: boolean;
    message: string;
    location?: Location;
    action_performed: string;
  }> {
    return apiService.post(`/admin/locations/submissions/${submissionId}/review`, review);
  }

  // Get all locations
  async getAllLocations(params?: {
    status?: string;
    category?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    locations: Location[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());

    const response: any = await apiService.get(`/admin/locations?${queryParams.toString()}`);
    return {
      locations: response.locations || [],
      total: response.total || 0,
      page: response.page || 1,
      page_size: response.page_size || 20,
      total_pages: response.total_pages || 1,
      has_next: response.has_next || false,
      has_previous: response.has_previous || false
    };
  }

  // Update location
  async updateLocation(locationId: string, updates: Partial<Location>): Promise<{
    success: boolean;
    message: string;
    location: Location;
  }> {
    return apiService.put(`/admin/locations/${locationId}`, updates);
  }

  // Enrich location with external data
  async enrichLocation(locationId: string): Promise<{
    success: boolean;
    message: string;
    location: Location;
  }> {
    return apiService.post(`/admin/locations/${locationId}/enrich`);
  }

  // Perform bulk actions on locations
  async performBulkAction(bulkAction: BulkLocationAction): Promise<{
    success: boolean;
    message: string;
    results: {
      successful: string[];
      failed: Array<{
        location_id: string;
        error: string;
      }>;
      total_processed: number;
    };
  }> {
    return apiService.post('/admin/locations/bulk-action', bulkAction);
  }

  // Get location analytics
  async getLocationAnalytics(): Promise<LocationAnalytics> {
    const response = await apiService.get<{
      success: boolean;
      message: string;
    } & LocationAnalytics>('/admin/locations/analytics');
    
    // Extract analytics data from response
    const { success, message, ...analytics } = response;
    return analytics;
  }

  // Export locations
  async exportLocations(params?: {
    format?: 'csv' | 'json';
    status?: string;
    category?: string;
  }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params?.format) queryParams.append('format', params.format);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.category) queryParams.append('category', params.category);

    const response = await apiService.get(`/admin/locations/export?${queryParams.toString()}`, {
      responseType: 'blob'
    });
    
    return response as unknown as Blob;
  }
}

export const locationService = new LocationService();